import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import {
  FaArrowLeft,
  FaUser,
  FaEye,
  FaShareAlt,
  FaPhone,
  FaWhatsapp,
  FaEllipsisV,
  FaEdit,
} from "react-icons/fa";
import TopNavbar from "../components/Gnavbar";
import BottomNavbar from "../components/BottomNavbar";
import Loader from "../components/Loader";
import { postRequest } from "../helpers/api.helper";
import { toast } from "react-toastify";
import { UPLOAD_URL } from "../utils/constants";
import { shareViaNative } from "../helpers/nativeShare";
import ShareModal from "../components/ShareModal";
import ShareBiodataModal from "../components/ShareBiodataModal";
import {
  getPrivacyOverlayLabel,
  shouldBlurProfileImage,
} from "../utils/imagePrivacy";
import { formatCreatedBy } from "../utils/createdBy";
import { useSelector } from "react-redux";
import { RootState } from "../store";

type AdvancedSearchCache = {
  criteriaKey: string;
  searchCriteria: Record<string, any>;
  profiles: any[];
  currentPage: number;
  hasMore: boolean;
  totalProfiles: number;
};

const CACHE_KEY = "advSearch:resultsCache";

const buildCriteriaKey = (searchData: Record<string, any>) =>
  JSON.stringify(
    Object.keys(searchData || {})
      .sort()
      .reduce<Record<string, any>>((acc, key) => {
        const value = searchData[key];
        acc[key] = Array.isArray(value) ? [...value].sort() : value;
        return acc;
      }, {}),
  );

const normalizeText = (value: unknown) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const AdvancedSearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const MybureauId = localStorage.getItem("bureauId");
  const [profiles, setProfiles] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [loadingNext, setLoadingNext] = useState(false);
  const [error, setError] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [profilesPerPage] = useState(10);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [autoLoadTimer, setAutoLoadTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareBiodataModalOpen, setIsShareBiodataModalOpen] = useState(false);
  const [shareBiodataType, setShareBiodataType] = useState<
    "pdf" | "image" | null
  >(null);
  const [selectedProfile, setSelectedProfile] = useState<any | null>(null);

  const loadMoreRef = useRef(null);

  const bureauInfo = useSelector((state: RootState) => state.bureau.data);

  const SCROLL_KEY_Y = "advSearch:lastScrollY";
  const SCROLL_KEY_PATH = "advSearch:lastPath";
  const SELECTED_PROFILE_KEY = "advSearch:selectedProfileId";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryData = Object.fromEntries(params.entries());
    const incomingSearchData =
      Object.keys(queryData).length > 0
        ? queryData
        : location.state?.searchData || null;

    if (!incomingSearchData) {
      toast.error("No search criteria provided");
      setLoading(false);
      return;
    }

    setSearchCriteria(incomingSearchData);

    try {
      const cacheRaw = sessionStorage.getItem(CACHE_KEY);
      const cache = cacheRaw
        ? (JSON.parse(cacheRaw) as AdvancedSearchCache)
        : null;
      const criteriaKey = buildCriteriaKey(incomingSearchData);

      if (
        cache &&
        cache.criteriaKey === criteriaKey &&
        Array.isArray(cache.profiles) &&
        cache.profiles.length > 0
      ) {
        setProfiles(cache.profiles);
        setCurrentPage(cache.currentPage || 1);
        setHasMore(Boolean(cache.hasMore));
        setTotalProfiles(cache.totalProfiles || cache.profiles.length);
        setLoading(false);
        return;
      }
    } catch (error) {
      console.warn("Unable to restore advanced search cache", error);
    }

    performAdvancedSearch(incomingSearchData, 1, false);
  }, [location.search, location.state]);

  const performAdvancedSearch = useCallback(
    async (searchData: any, pageNum = 1, append = false) => {
      if (!searchData) return;
      if (!append) {
        setLoading(true);
      } else {
        setLoadingNext(true);
      }
      setError(null);
      try {
        const response = await postRequest(`/bureau/search/advanced`, {
          ...searchData,
          page: pageNum,
          limit: profilesPerPage,
        });

        if (!response.success) {
          toast.error(response.message || "Failed to fetch results.");
          setHasMore(false);
          if (!append) setProfiles([]);
          return;
        }

        const data = (response.data as any) ?? {};
        const users = data.users;
        const total =
          data.total ?? data.count ?? (Array.isArray(users) ? users.length : 0);

        if (Array.isArray(users) && users.length > 0) {
          if (append) {
            // @ts-ignore
            setProfiles((prev) => [...prev, ...users]);
          } else {
            // @ts-ignore
            setProfiles(users);
          }
          setTotalProfiles(total);
          setCurrentPage(pageNum);
          const totalPages = Math.ceil(total / profilesPerPage);
          setHasMore(pageNum < totalPages);
          if (append && pageNum < totalPages) {
            startAutoLoadTimer();
          } else if (!append) {
            startAutoLoadTimer();
          }
        } else {
          setHasMore(false);
          if (!append) setProfiles([]);
        }
      } catch (error) {
        console.error("Error performing advanced search:", error);
        toast.error("Error performing search. Please try again.");
        if (!append) setProfiles([]);
      } finally {
        setLoading(false);
        setLoadingNext(false);
      }
    },
    [profilesPerPage],
  );

  useEffect(() => {
    if (!searchCriteria || !Array.isArray(profiles) || profiles.length === 0) {
      return;
    }

    const cache: AdvancedSearchCache = {
      criteriaKey: buildCriteriaKey(searchCriteria),
      searchCriteria,
      profiles,
      currentPage,
      hasMore,
      totalProfiles,
    };

    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn("Unable to persist advanced search cache", error);
    }
  }, [searchCriteria, profiles, currentPage, hasMore, totalProfiles]);

  // Start auto-load timer
  const startAutoLoadTimer = useCallback(() => {
    if (autoLoadTimer) clearTimeout(autoLoadTimer);
    const timer = setTimeout(() => {
      if (hasMore) {
        handleLoadNext();
      }
    }, 15000);
    setAutoLoadTimer(timer);
  }, [hasMore]);

  // Manual load next batch
  const handleLoadNext = useCallback(() => {
    if (autoLoadTimer) clearTimeout(autoLoadTimer);
    if (hasMore && !loadingNext && searchCriteria) {
      performAdvancedSearch(searchCriteria, currentPage + 1, true);
    }
  }, [
    hasMore,
    loadingNext,
    currentPage,
    searchCriteria,
    performAdvancedSearch,
    autoLoadTimer,
  ]);

  // Initial load
  useEffect(() => {
    return () => {
      if (autoLoadTimer) clearTimeout(autoLoadTimer);
    };
  }, [autoLoadTimer]);

  useEffect(() => {
    const handleShareBiodata = (event: any) => {
      const { type } = event.detail;
      setShareBiodataType(type);
      setIsShareBiodataModalOpen(true);
    };

    window.addEventListener("shareBiodata", handleShareBiodata);
    return () => {
      window.removeEventListener("shareBiodata", handleShareBiodata);
    };
  }, []);

  // Intersection Observer for scroll-based loading
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingNext && !loading) {
          handleLoadNext();
        }
      },
      { threshold: 0.1 },
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [hasMore, loadingNext, loading]);

  const handleBackClick = () => {
    navigate("/admin/search");
  };

  const buildSharableSearchUrl = useCallback(() => {
    const bureauId = bureauInfo?.bureauId || MybureauId;
    if (!bureauId || !searchCriteria) {
      return "";
    }

    const cleanName = (bureauInfo?.bureauName || "bureau").replace(/\s+/g, "");
    const baseShareHost = Capacitor.isNativePlatform()
      ? "https://matrimonystudio.in"
      : window.location.origin;
    const params = new URLSearchParams();

    Object.entries(searchCriteria).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === "Any"
      ) {
        return;
      }

      if (Array.isArray(value)) {
        if (value.length) {
          params.append(key, value.join(","));
        }
        return;
      }

      params.append(key, String(value));
    });

    const baseUrl = `${baseShareHost}/${bureauId}/${encodeURIComponent(cleanName)}/search/results`;
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
  }, [
    MybureauId,
    bureauInfo?.bureauId,
    bureauInfo?.bureauName,
    searchCriteria,
  ]);

  const handleShareSearch = async () => {
    const sharableUrl = buildSharableSearchUrl();
    if (!sharableUrl) {
      toast.error("Search link is not ready yet.");
      return;
    }

    try {
      await shareViaNative({
        title: "Advanced Search Results",
        text: "Open these filtered matrimony search results.",
        url: sharableUrl,
      });
    } catch (error) {
      try {
        await navigator.clipboard.writeText(sharableUrl);
        toast.success("Search link copied");
      } catch (copyError) {
        console.error("Failed to share search link", error);
        console.error("Failed to copy search link", copyError);
        toast.error("Unable to share search link");
      }
    }
  };

  const handleProfileClick = (profileId: string, index: number) => {
    // ✅ Save scroll before leaving
    try {
      sessionStorage.setItem(SCROLL_KEY_Y, String(window.scrollY));
      sessionStorage.setItem(
        SCROLL_KEY_PATH,
        window.location.pathname + window.location.search,
      );
      sessionStorage.setItem(SELECTED_PROFILE_KEY, profileId);
    } catch {}

    navigate(`/admin/profile/detail/${profileId}`, {
      state: {
        source: "advanced-search-results",
        returnTo: window.location.pathname + window.location.search,
        searchData: searchCriteria,
        profileIds: profiles
          .map((item: any) => item?.martialId || item?.maritalId)
          .filter(Boolean),
        currentIndex: index,
        selectedProfileId: profileId,
      },
    });
  };

  const toggleDropdown = (profileId: any) => {
    setOpenDropdown((prev) => (prev === profileId ? null : profileId));
  };

  const openShareModal = (profile: any) => {
    setSelectedProfile(profile);
    setIsShareModalOpen(true);
  };

  const calculateAge = (dob: any) => {
    const birthDate = new Date(dob);
    const currentDate = new Date();
    let age = currentDate.getFullYear() - birthDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    if (
      month < birthDate.getMonth() ||
      (month === birthDate.getMonth() && day < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    if (loading || profiles.length === 0) return;

    try {
      const lastPath = sessionStorage.getItem(SCROLL_KEY_PATH);
      const currentPath = window.location.pathname + window.location.search;

      if (!lastPath || lastPath !== currentPath) return;

      const y = Number(sessionStorage.getItem(SCROLL_KEY_Y) || 0);
      const selectedProfileId = sessionStorage.getItem(SELECTED_PROFILE_KEY);

      // wait for layout render
      const restore = () => {
        window.scrollTo({ top: y, behavior: "auto" });

        // If still not enough height, try again after small delay
        setTimeout(() => {
          if (Math.abs(window.scrollY - y) > 150) {
            window.scrollTo({ top: y, behavior: "auto" });
          }

          if (selectedProfileId) {
            const target = document.getElementById(
              `adv-profile-${selectedProfileId}`,
            );
            target?.scrollIntoView({ block: "center", behavior: "auto" });
          }
        }, 300);
      };

      requestAnimationFrame(restore);
    } catch {}
  }, [loading, profiles.length, location.key]);

  if (loading)
    return (
      <div className="flex flex-col bg-white min-h-screen">
        <TopNavbar />
        <div className="flex-1 p-4 mt-20 mb-20">
          <div className="flex justify-center items-center py-8">
            <Loader />
          </div>
        </div>
        <BottomNavbar />
      </div>
    );

  if (error)
    return (
      <div className="flex flex-col bg-white min-h-screen">
        <TopNavbar />
        <div className="flex-1 p-4 mt-20 mb-20">
          <div className="text-center py-8">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={() => navigate("/admin/search")}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Try Different Search
            </button>
          </div>
        </div>
        <BottomNavbar />
      </div>
    );

  return (
    <div className="flex flex-col bg-white min-h-screen">
      <div className="flex-1 p-4 mb-20">
        {/* Header */}
        <div className="flex items-center mb-6">
          <FaArrowLeft
            className="mr-3 text-gray-600 cursor-pointer text-xl"
            onClick={() => navigate(-1)}
          />
          <div className="flex flex-1 items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-gray-800">
              Advanced Search Results
            </h1>
          </div>
        </div>

        {/* Search Criteria Summary */}
        {searchCriteria && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-blue-800 mb-2">
              Search Criteria
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
              {searchCriteria.gender && (
                <span className="bg-blue-100 px-2 py-1 rounded">
                  <strong>Gender:</strong>{" "}
                  {searchCriteria.gender === "male" ? "Male" : "Female"}
                </span>
              )}
              {searchCriteria.ageMin && searchCriteria.ageMax && (
                <span className="bg-blue-100 px-2 py-1 rounded">
                  <strong>Age:</strong> {searchCriteria.ageMin} -{" "}
                  {searchCriteria.ageMax} years
                </span>
              )}
              {searchCriteria.heightMin && searchCriteria.heightMax && (
                <span className="bg-blue-100 px-2 py-1 rounded">
                  <strong>Height:</strong> {searchCriteria.heightMin} -{" "}
                  {searchCriteria.heightMax} ft
                </span>
              )}
              {(() => {
                const maritalStatusPreference =
                  searchCriteria.maritalStatusPreferences ??
                  searchCriteria.maritalStatusPreference ??
                  searchCriteria.maritalStatus;

                const maritalStatusLabel = Array.isArray(
                  maritalStatusPreference,
                )
                  ? maritalStatusPreference
                      .filter(
                        (item) =>
                          item &&
                          String(item).trim() &&
                          String(item).toLowerCase() !== "any",
                      )
                      .join(", ")
                  : maritalStatusPreference &&
                      String(maritalStatusPreference).trim() &&
                      String(maritalStatusPreference).toLowerCase() !== "any"
                    ? String(maritalStatusPreference)
                    : "";

                return maritalStatusLabel ? (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Marital Status:</strong> {maritalStatusLabel}
                  </span>
                ) : null;
              })()}
              {searchCriteria.religionPreferences &&
                searchCriteria.religionPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Religion:</strong>{" "}
                    {searchCriteria.religionPreferences}
                  </span>
                )}
              {searchCriteria.castePreferences &&
                searchCriteria.castePreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Caste:</strong> {searchCriteria.castePreferences}
                  </span>
                )}
              {searchCriteria.educationPreferences &&
                searchCriteria.educationPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Education:</strong>{" "}
                    {searchCriteria.educationPreferences}
                  </span>
                )}
              {searchCriteria.occupationPreferences &&
                searchCriteria.occupationPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Occupation:</strong>{" "}
                    {searchCriteria.occupationPreferences}
                  </span>
                )}
              {searchCriteria.countryPreferences &&
                searchCriteria.countryPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Country:</strong>{" "}
                    {searchCriteria.countryPreferences}
                  </span>
                )}
              {searchCriteria.statePreferences &&
                searchCriteria.statePreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>State:</strong> {searchCriteria.statePreferences}
                  </span>
                )}
              {searchCriteria.cityPreferences &&
                searchCriteria.cityPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>City:</strong> {searchCriteria.cityPreferences}
                  </span>
                )}
              {searchCriteria.citizenshipPreferences &&
                searchCriteria.citizenshipPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Citizenship:</strong>{" "}
                    {searchCriteria.citizenshipPreferences}
                  </span>
                )}
            </div>
          </div>
        )}

        {/* Results */}
        <div className="mb-4">
          <p className="text-gray-600">
            Found {totalProfiles} profile{totalProfiles !== 1 ? "s" : ""}{" "}
            matching your criteria
          </p>
          {searchCriteria && (
            <button
              type="button"
              onClick={handleShareSearch}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 mt-2 w-full sm:w-auto justify-center"
            >
              <FaShareAlt size={16} />
              Share All Profiles
            </button>
          )}
        </div>

        {profiles.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              No profiles found matching your search criteria.
            </p>
            <button
              onClick={() => navigate("/admin/search")}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Modify Search
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-32">
              {profiles.map((profile: any, index: any) => (
                <div
                  key={`${profile._id}-${index}`}
                  id={`adv-profile-${profile.martialId || profile.maritalId}`}
                  className="bg-white shadow-lg rounded-lg p-4 flex flex-col transition-transform duration-200 hover:shadow-xl hover:scale-105"
                >
                  {/* Profile Header with three-dots menu */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h2 className="text-lg font-bold">{profile.fullName}</h2>
                      <span className="text-sm font-bold">
                        {calculateAge(profile.dateOfBirth)} yrs |{" "}
                        {profile.height} ft
                      </span>
                      {normalizeText(profile.citizenship) === "nri" && (
                        <p className="text-sm font-bold text-red-600">
                          NRI PROFILE
                        </p>
                      )}
                    </div>
                    <div className="relative dropdown-container">
                      {MybureauId === profile.bureauId ? (
                        <>
                          {/* Call Button */}
                          <a
                            href={`tel:${profile.mobileNumber}`}
                            className="bg-blue-500 text-white px-3 py-1 rounded-md flex items-center text-xs mr-2"
                          >
                            <FaPhone size={14} />
                            <span className="ml-1">Call</span>
                          </a>

                          {/* WhatsApp Button */}
                          <a
                            href={`https://wa.me/${profile.countryCode}${profile.mobileNumber}`}
                            className="bg-green-500 text-white px-3 py-1 rounded-md flex items-center text-xs mr-2"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaWhatsapp size={14} />
                            <span className="ml-1">WhatsApp</span>
                          </a>

                          {/* Dropdown Options */}
                          <button
                            onClick={() => toggleDropdown(profile._id)}
                            className="text-gray-500 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors"
                          >
                            <FaEllipsisV size={16} />
                          </button>
                          {openDropdown === profile._id && (
                            <div className="absolute right-0 mt-2 bg-white border rounded shadow-md z-10 w-48">
                              <a
                                href={`/admin/edit-profile/${profile._id}`}
                                className="block w-full text-left px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50 items-center"
                              >
                                <FaEdit className="mr-2" size={14} />
                                Edit Profile
                              </a>
                              <div className="px-4 py-2 text-sm text-gray-600">
                                Own Bureau Profile
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-gray-600 px-3 py-1 rounded bg-green-300 text-xs">
                          {formatCreatedBy(profile.createdBy)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex">
                    <div className="flex-1 text-sm">
                      <p className="text-gray-700">
                        Marital ID: {profile.martialId}
                      </p>
                      <p>
                        Address: {profile.originalLocation || profile.district}
                      </p>
                      <p>
                        Caste: {profile.caste} - {profile.subcaste}
                      </p>
                      <p>Degree: {profile.education}</p>
                      <p>Income: ₹ {profile.annualIncome}</p>
                      <p>Profession: {profile.occupation}</p>
                      <p>
                        {profile.district}, {profile.state}, {profile.country}
                      </p>
                    </div>
                    {(() => {
                      const blurPhoto = shouldBlurProfileImage(
                        profile.imagePrivacy,
                      );
                      const fallback =
                        "https://www.pngitem.com/pimgs/m/581-5813504_avatar-dummy-png-transparent-png.png";
                      const imageSrc = profile.image
                        ? `${UPLOAD_URL}/users/profile_pic/${profile.image}`
                        : fallback;
                      return (
                        <div className="relative ml-4 w-28 h-36 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={imageSrc}
                            alt="Profile"
                            className={`w-full h-full object-cover ${
                              blurPhoto ? "blur-sm scale-110 brightness-90" : ""
                            }`}
                          />
                        </div>
                      );
                    })()}
                  </div>

                  <div className="flex justify-around items-center mt-4 text-gray-800 space-x-6">
                    {MybureauId === profile.bureauId ? (
                      <div
                        className="text-center cursor-pointer"
                        onClick={() =>
                          handleProfileClick(profile.maritalId, index)
                        }
                      >
                        <FaUser className="text-blue-500 mb-1" size={20} />
                        <span className="text-xs">View More</span>
                      </div>
                    ) : (
                      <div
                        className="text-center cursor-pointer"
                        onClick={() =>
                          handleProfileClick(profile.martialId, index)
                        }
                      >
                        <FaUser className="text-blue-500 mb-1" size={20} />
                        <span className="text-xs">View More</span>
                      </div>
                    )}
                    <div className="text-center">
                      <FaEye className="text-green-500 mb-1" size={20} />
                      <span className="text-xs">
                        {profile.views || 0} Views
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-center"
                      onClick={() => openShareModal(profile)}
                    >
                      <FaShareAlt className="text-green-500 mb-1" size={20} />
                      <span className="text-xs">Share</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Section */}
            <div className="mt-8 mb-4">
              <div ref={loadMoreRef} className="h-4"></div>
              {loadingNext && (
                <div
                  style={{
                    position: "fixed",
                    left: 0,
                    width: "100%",
                    bottom: "20%",
                    zIndex: 50,
                    display: "flex",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <div style={{ pointerEvents: "auto" }}>
                    <Loader />
                    <span className="ml-2 text-gray-600">
                      Loading next batch...
                    </span>
                  </div>
                </div>
              )}
              {hasMore && !loadingNext && (
                <div className="flex justify-center">
                  <button
                    onClick={handleLoadNext}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <span>Load Next 10 Profiles</span>
                    <span className="text-xs bg-blue-500 px-2 py-1 rounded">
                      Batch {Math.ceil(profiles.length / profilesPerPage) + 1}
                    </span>
                  </button>
                </div>
              )}
              {!hasMore && profiles.length > 0 && (
                <div className="text-center text-gray-500 py-4">
                  <p>All profiles loaded</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {isShareModalOpen && selectedProfile && (
        <ShareModal
          profile={selectedProfile}
          setIsShareModalOpen={setIsShareModalOpen}
        />
      )}

      {isShareBiodataModalOpen && shareBiodataType && selectedProfile && (
        <ShareBiodataModal
          profile={selectedProfile}
          setIsShareBiodataModalOpen={setIsShareBiodataModalOpen}
          shareType={shareBiodataType}
        />
      )}

      <BottomNavbar />
    </div>
  );
};

export default AdvancedSearchResults;
